import React from "react";
import {
  Button,
  EditAction,
  OneLineList,
  useIsMobile,
} from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import {
  type GateProps,
  type SubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
import {
  EnquiryDetail,
  EnquiryPaymentDetail,
  EnquiryPaymentStatus,
  EnquiryStatus,
} from "../types/enquiries.types";

/** The same four tints the enquiries table uses, so a status reads the same on
 *  the row it was picked from and the record it opens. The border is the fill:
 *  these are flat pills, and the outlined ones no longer match the table. */
const STATUS_STYLES: Record<EnquiryStatus, string> = {
  "Recently Added": "border-[#EFFBFD] bg-[#EFFBFD] text-[#007E8C]",
  Interested: "border-[#ECFFEB] bg-[#ECFFEB] text-[#1A8810]",
  "Not interested": "border-[#F9F9FB] bg-[#F9F9FB] text-[#62636C]",
  "Payment sent": "border-[#FFF7D9] bg-[#FFF7D9] text-[#917A2E]",
  Converted: "border-[#E7F8EF] bg-[#E7F8EF] text-[#2F8E5B]",
};

/** Label followed by its value, the repeating unit of every card here. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  // On a phone every field takes a line of its own — `basis-full` — the same
  // single column the grid rows below fall to, rather than the wrapping rows
  // pairing "Gender" with "DOB" and leaving "Email" alone under them.
  //
  // `min-w-0` is what lets it fit at all: a flex or grid item keeps its
  // content's width by default, so a long value here — a client with three
  // goals — pushed its card wider than the screen and the whole page scrolled
  // sideways. Free to shrink, the field is given the card's width and a list
  // inside it counts what did not fit instead.
  <div className="flex min-w-0 basis-full items-baseline gap-2 sm:basis-auto">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}:
    </span>
    {/* `min-w-0` so a value that measures itself against this cell — the goal
        line below — is given the cell's width rather than its own content's,
        which would never overflow and so never count anything. */}
    <span className="min-w-0 text-[14px] 3xl:text-[15px] font-medium text-ink">
      {value}
    </span>
  </div>
);

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="card-surface border border-line bg-white">{children}</section>
);

const PAYMENT_STATUS_STYLES: Record<EnquiryPaymentStatus, string> = {
  Pending: "border-[#F3D9A8] bg-[#FDF7EC] text-[#B27A16]",
  Paid: "border-[#BEE9CF] bg-[#F3FBF6] text-[#2F8E5B]",
  // The grey a called-off request wears in a client's payment history: nothing
  // is owed and nothing is coming, so it recedes rather than colouring.
  Cancelled: "border-line bg-[#F1F4F6] text-ink-muted",
};

/**
 * The payment requested from this enquiry — what stands where the Request
 * payment button was, once one has gone out.
 *
 * Confirming is offered while it is pending because nothing reports the money
 * in yet: a client who paid by UPI or in cash is marked off here. Declining is
 * the other answer, and the only way a request that is never going to be paid —
 * the wrong plan, the wrong price, an enquiry who changed their mind — stops
 * standing as money owed.
 */
const PaymentRow: React.FC<{
  payment: EnquiryPaymentDetail;
  /** Both answers, off together while the workspace has no plan behind it. */
  gate: GateProps;
  onConfirm: () => void;
  onDecline: () => void;
  /** The padding its surroundings want — see `PaymentSection`. */
  className: string;
}> = ({ payment, gate, onConfirm, onDecline, className }) => (
  <div
    className={`flex flex-wrap items-center justify-between gap-4 ${className}`}
  >
    <div className="flex items-center gap-3">
      {/* The tile carries the currency, so the amount beside it is written as
          a plain number — a second ₹ an inch away would read as two prices. */}
      <span
        aria-hidden="true"
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#FDF0DC] text-[16px] font-medium text-[#C4881F]"
      >
        ₹
      </span>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="text-[18px] 3xl:text-[20px] font-medium text-ink"
            // What is written is the number; what is read out is the money,
            // and the plan it is for.
            aria-label={`${formatPrice(payment.amount)} for ${payment.planName}, ${payment.duration}`}
          >
            {payment.amount.toLocaleString("en-IN")}
          </span>
          {/* Smaller than the enquiry's own pill above it: this one labels the
              amount beside it rather than the record, and at the header's size
              it reads as the second status of the page. */}
          <span
            className={`inline-flex rounded-full border px-2 py-px text-[10px] font-medium leading-[13px] ${PAYMENT_STATUS_STYLES[payment.status]}`}
          >
            {payment.status}
          </span>
        </div>
        <div className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          Requested on:{" "}
          <span className="font-medium text-ink">{payment.requestedOn}</span>
        </div>
      </div>
    </div>

    {/* Both answers together, the confirm leading: it is the one that happens,
        and the decline is the exception beside it rather than a choice of
        equals. Neither commits anything on its own — each opens its question
        first, because nothing takes either answer back. */}
    {payment.status === "Pending" && (
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onConfirm} {...gate}>
          Confirm payment
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onDecline}
          {...gate}
        >
          Payment declined
        </Button>
      </div>
    )}
  </div>
);

/**
 * What this enquiry owes: the request once one has gone out, and the button
 * that raises it until then.
 *
 * The request replaces the button that raised it: there is one payment per
 * enquiry, so an offer to raise a second beside the first would only ever be a
 * mistake waiting to be clicked.
 *
 * It is one component rather than markup written twice because a phone and a
 * desktop hang it in different cards — see `EnquiryDetailsView` — and two
 * copies of a button under a `hidden` class would be two buttons on the page
 * to anything reading it rather than looking at it.
 */
const PaymentSection: React.FC<{
  enquiry: EnquiryDetail;
  gate: SubscriptionGate;
  onRequestPayment: () => void;
  onConfirmPayment: () => void;
  onDeclinePayment: () => void;
  /** The padding the card it is standing in wants. */
  className: string;
  /** Whether the button spans its card — a phone's shape; see the callers. */
  fullWidth: boolean;
}> = ({
  enquiry,
  gate,
  onRequestPayment,
  onConfirmPayment,
  onDeclinePayment,
  className,
  fullWidth,
}) =>
  enquiry.payment ? (
    <PaymentRow
      payment={enquiry.payment}
      gate={gate.props("answer a payment")}
      onConfirm={onConfirmPayment}
      onDecline={onDeclinePayment}
      className={className}
    />
  ) : (
    <div className={className}>
      <Button
        size="sm"
        fullWidth={fullWidth}
        onClick={onRequestPayment}
        // "Payment sent" with no payment on the record is an enquiry moved
        // by hand from the list's row menu; there is nothing to show for
        // it, and nothing to raise either. That one stays plainly
        // disabled — its own label says why, so there is no press to
        // answer. Anything else wears the workspace's answer instead.
        {...(enquiry.status === "Payment sent"
          ? { disabled: true }
          : gate.props("request a payment"))}
      >
        {enquiry.status === "Payment sent" ? (
          "Payment requested"
        ) : (
          <>
            {/* Its own element rather than spaces in the label: a run of
                them collapses to one space, which is not the gap the
                design has. `mr-2` matches the `gap-2` the button gives a
                real `startIcon`, so this sits like every other icon in the
                app. Hidden from the accessibility tree — "plus Request
                payment" is not what the button does. */}
            <span aria-hidden="true" className="mr-1">
              +
            </span>
            Request payment
          </>
        )}
      </Button>
    </div>
  );

interface EnquiryDetailsViewProps {
  enquiry: EnquiryDetail;
  /**
   * Whether this workspace may still act on the enquiry, read once by the
   * screen above — see `EnquiryDetailPage`. Everything here writes: the record,
   * the stage it is at, and the money asked for it. The details themselves stay
   * readable, the way the roster and the list do.
   */
  gate: SubscriptionGate;
  /** Opens the Create new payment dialog. */
  onRequestPayment: () => void;
  /** Marks the requested payment as received. */
  onConfirmPayment: () => void;
  /** Calls the requested payment off — asked before anything is called off. */
  onDeclinePayment: () => void;
  /** Opens the edit form, filled with this enquiry's own answers. */
  onEdit: () => void;
}

const EnquiryDetailsView: React.FC<EnquiryDetailsViewProps> = ({
  enquiry,
  gate,
  onRequestPayment,
  onConfirmPayment,
  onDeclinePayment,
  onEdit,
}) => {
  // Which card the payment hangs in is the one thing here a stylesheet cannot
  // decide: on a phone it belongs to the header, under the name it is owed
  // for, and on a desktop it is the last card of the page. Rendering it in
  // both and hiding one would put two Request payment buttons on the page.
  const isMobile = useIsMobile();

  const payment = (className: string) => (
    <PaymentSection
      enquiry={enquiry}
      gate={gate}
      onRequestPayment={onRequestPayment}
      onConfirmPayment={onConfirmPayment}
      onDeclinePayment={onDeclinePayment}
      className={className}
      // A phone gives it the card's width: it is the one thing the screen is
      // asking to be pressed, and a short button adrift at the left of a
      // full-width card reads as an aside. A desktop card is wide enough that
      // the same button would stretch halfway across the page, so there it
      // stays the width of its own words.
      fullWidth={isMobile}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3 px-5 sm:px-6 py-5">
          <span className="w-11 h-11 rounded-full bg-[#DEEBFB] flex items-center justify-center flex-shrink-0">
            <span className="text-[16px] font-medium text-[#2F6BAE]">
              {enquiry.name.charAt(0)}
            </span>
          </span>
          {/* On a phone the name and the badge stack beside the avatar, so the
              badge is not competing with the button for the tail of a wrapping
              row — which left it squeezed against the card's right edge with the
              button hanging under it. The same arrangement a client's header
              takes; see `ClientDetailsTab`.

              From `sm` the wrapper stops being a box at all — see `contents` —
              and the two are children of the header row again, exactly as they
              were. */}
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 sm:contents">
            <h2 className="text-[18px] sm:text-[20px] font-medium text-ink">
              {enquiry.salutation} {enquiry.name}
            </h2>
            <span
              className={`inline-flex flex-shrink-0 rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${STATUS_STYLES[enquiry.status]}`}
            >
              {enquiry.statusLabel}
            </span>
          </div>

          {/* Pushed to the end of the row so it reads as an action on the header
              rather than another badge. A stage is moved from the list's row
              menu, so opening the whole record for editing is all this header
              offers.

              A pencil on a phone, level with the name it edits — the same shape
              a client's header takes; see `EditAction` and `ClientDetailsTab`. */}
          <EditAction
            label="Edit details"
            className="ml-auto max-md:self-start"
            onClick={onEdit}
            {...gate.props("edit an enquiry")}
          />
        </div>

        {/* A phone asks for the money from the header: the button is what the
            screen is for, and at the foot of the record it was below the fold
            and behind the tab bar. It keeps the header's own padding and no
            rule above it, so it reads as part of the name's block rather than a
            section of its own.

            Only the button comes up here. Once the request has gone out what
            stands in its place is a record of it — an amount, a status and a
            date — and a record belongs with the other records below, not in
            the header where an action was. */}
        {isMobile && !enquiry.payment && payment("px-5 pb-5")}

        {/* Spread edge to edge so the last field lines up with the card's right
            padding, the way the design has it. */}
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 border-t border-line px-5 sm:px-6 py-4">
          <Field label="Gender" value={enquiry.gender} />
          <Field label="DOB" value={enquiry.dob} />
          <Field label="Age" value={enquiry.age ?? "-"} />
          <Field label="Life style" value={enquiry.lifestyle} />
          <Field label="Profession" value={enquiry.profession} />
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
          <Field
            label="Goal"
            value={<OneLineList items={enquiry.goals} label="goals" />}
          />
          <Field label="Requested via" value={enquiry.requestedVia} />
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
          <Field label="Phone" value={<DiallingCode>{enquiry.phone}</DiallingCode>} />
          <Field
            label="WhatsApp"
            value={<DiallingCode>{enquiry.whatsapp}</DiallingCode>}
          />
          <Field label="Email" value={enquiry.email} />
        </div>
        <div className="border-t border-line px-5 sm:px-6 py-4">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Address:
          </div>
          <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
            {enquiry.address}
          </div>
        </div>
      </Card>

      {/* Its own card at the foot of the record: where the design has it, and
          on a phone where the raised request stays even though the button that
          raises one has gone up to the header. */}
      {(!isMobile || enquiry.payment) && (
        <Card>
          {payment(
            enquiry.payment ? "px-5 sm:px-6 py-4" : "px-5 sm:px-6 py-5"
          )}
        </Card>
      )}
    </div>
  );
};

export default EnquiryDetailsView;
